import type { MetricCostConfig } from "@models/model";

export interface MetadataEntry {
  key: string;
  value: string;
}

export interface PricingTier {
  tierStart: number | "";
  tierEnd: number | "";
  price: number | "";
}

export interface FormState {
  modelName: string;
  displayName: string;
  status: string;
  metadata: MetadataEntry[];
  inputTiers: PricingTier[];
  outputTiers: PricingTier[];
  currency: string;
}

export const emptyTier = (): PricingTier => ({
  tierStart: "",
  tierEnd: "",
  price: "",
});

export const emptyForm = (): FormState => ({
  modelName: "",
  displayName: "",
  status: "active",
  metadata: [],
  inputTiers: [emptyTier()],
  outputTiers: [emptyTier()],
  currency: "USD",
});

export function extractTiers(
  costConfig: Record<string, MetricCostConfig>,
  prefix: string,
): MetricCostConfig[] {
  if (costConfig[prefix]) return [costConfig[prefix]];
  const tiers: MetricCostConfig[] = [];
  let i = 0;
  while (costConfig[`${prefix}_${i}`])
    tiers.push(costConfig[`${prefix}_${i++}`]);
  return tiers;
}

export function tiersToFormTiers(tiers: MetricCostConfig[]): PricingTier[] {
  if (tiers.length === 0) return [emptyTier()];
  return tiers.map((t) => ({
    price: t.unit_cost,
    tierStart: t.tier_start ?? "",
    tierEnd: t.tier_end ?? "",
  }));
}

export function formatTierSummary(
  tiers: MetricCostConfig[],
  currency: string,
): string {
  if (tiers.length === 0) return "-";
  const sym = currency === "USD" ? "$" : currency + " ";
  if (tiers.length === 1) {
    const p = tiers[0].unit_cost;
    return `${sym}${p < 0.01 ? p.toFixed(6) : p.toFixed(2)}/1M`;
  }
  return `${tiers.length} tiers`;
}

export function metadataToRecord(
  entries: MetadataEntry[],
): Record<string, string> {
  return Object.fromEntries(
    entries.filter((e) => e.key.trim()).map((e) => [e.key.trim(), e.value]),
  );
}
